import { Link } from "react-router-dom";
import { WORKS, GENRES } from "@/mocks/works";
import { FAQS } from "@/mocks/faq";
import WorkCard from "@/components/base/WorkCard";
import { usePageMeta } from "@/hooks/usePageMeta";

const HOT = [...WORKS].sort((a, b) => b.popularity - a.popularity).slice(0, 5);
const TOP = [...WORKS].sort((a, b) => b.rating - a.rating).slice(0, 8);

const STATS = [
  { value: "500+", label: "收录作品" },
  { value: "12", label: "热门题材" },
  { value: "9.6", label: "最高评分" },
  { value: "每周更新", label: "榜单动态" },
];

export default function Home() {
  usePageMeta({
    title: "人气韩漫大全｜韩漫人气排行榜、高分作品推荐与追更指南",
    description:
      "人气韩漫大全汇集人气韩漫排行榜、高分韩漫推荐与题材分类，实时更新热门韩漫热度排名，帮你快速锁定当前最值得追的韩漫，追更不迷路。",
    path: "/",
  });

  return (
    <>
      <section className="relative flex min-h-[92vh] w-full items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=vibrant%20stylized%20Korean%20webtoon%20themed%20editorial%20banner%20with%20floating%20comic%20panels%2C%20dynamic%20action%20character%20silhouettes%2C%20open%20books%20and%20pages%20flying%20in%20the%20air%2C%20warm%20coral%20orange%20cream%20and%20golden%20color%20palette%2C%20soft%20cinematic%20lighting%2C%20modern%20clean%20illustration%2C%20high%20detail&width=1600&height=900&seq=hero-home&orientation=landscape"
          alt="人气韩漫大全 精选韩漫背景插画"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/60"></div>
        <div className="relative z-10 mx-auto w-full max-w-6xl px-4 py-24 text-center md:px-6">
          <p className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-sm font-medium text-background-50 backdrop-blur">
            <i className="ri-fire-fill text-secondary-400"></i>
            热门韩漫 · 榜单每日更新
          </p>
          <h1 className="mt-6 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl lg:text-6xl">
            人气韩漫大全
            <span className="mt-2 block text-secondary-300">热门韩漫一网打尽，追更不迷路</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-background-100 md:text-lg">
            想看韩漫又怕踩雷？人气韩漫大全排行榜、推荐榜、热度排名实时更新，高分作品一目了然，帮你快速锁定当前最值得追的韩漫。
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/ranking"
              className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              查看人气排行榜
            </Link>
            <Link
              to="/top-rated"
              className="whitespace-nowrap rounded-full border border-background-50/40 bg-background-50/10 px-7 py-3 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              浏览高分佳作
            </Link>
          </div>
          <dl className="mx-auto mt-12 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.label} className="rounded-xl bg-background-50/10 px-4 py-3 backdrop-blur">
                <dd className="font-heading text-xl font-bold text-background-50 md:text-2xl">{s.value}</dd>
                <dt className="mt-0.5 text-xs text-background-200">{s.label}</dt>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="font-label text-xs font-semibold uppercase tracking-widest text-primary-600">Hot Ranking</p>
            <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">本周人气韩漫榜</h2>
          </div>
          <Link to="/ranking" className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700">
            查看完整榜单 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {HOT.map((work, i) => (
            <li key={work.id} className="group overflow-hidden rounded-xl border border-background-200 bg-background-50">
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={work.image}
                  alt={`${work.title} 韩漫封面`}
                  title={`${work.title} 人气韩漫`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <span className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                  {i + 1}
                </span>
                <span className="absolute bottom-3 right-3 rounded-full bg-foreground-950/70 px-2 py-0.5 text-xs text-background-50 backdrop-blur">
                  <i className="ri-fire-line mr-1 text-secondary-400"></i>
                  {work.popularity}
                </span>
              </div>
              <div className="p-3">
                <h3 className="truncate text-sm font-bold text-foreground-950">{work.title}</h3>
                <p className="mt-1 text-xs text-foreground-500">
                  <i className="ri-star-fill mr-1 text-secondary-500"></i>
                  {work.rating.toFixed(1)} · {work.genres[0]}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="font-label text-xs font-semibold uppercase tracking-widest text-accent-600">Genres</p>
              <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">按题材挑选韩漫</h2>
            </div>
            <Link to="/genres" className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-accent-600 transition-colors hover:text-accent-700">
              全部题材 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {GENRES.map((g) => (
              <Link
                key={g.name}
                to={`/genres?g=${encodeURIComponent(g.name)}`}
                className="group flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-accent-300"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                  <i className="ri-palette-line text-xl"></i>
                </span>
                <span className="min-w-0">
                  <span className="block font-heading text-lg font-bold text-foreground-950">{g.name}</span>
                  <span className="mt-0.5 block truncate text-xs text-foreground-500">{g.desc}</span>
                </span>
                <span className="ml-auto whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                  {g.count}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="font-label text-xs font-semibold uppercase tracking-widest text-secondary-600">Top Rated</p>
            <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">高分韩漫佳作</h2>
          </div>
          <Link to="/top-rated" className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-secondary-700">
            查看全部高分 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {TOP.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="font-label text-xs font-semibold uppercase tracking-widest text-primary-600">追更须知</p>
              <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">新手追更常见问题</h2>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-foreground-600">
                第一次追韩漫不知道从哪下手？我们整理了读者最常问的几个问题，帮你快速了解榜单规则与追更方式。
              </p>
              <Link
                to="/faq"
                className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部问答 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <ul className="divide-y divide-background-200 rounded-xl border border-background-200 bg-background-50">
              {FAQS.slice(0, 4).map((f) => (
                <li key={f.q} className="p-5">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                      <i className="ri-question-line text-sm"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">{f.q}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20warm%20gradient%20banner%20with%20soft%20orange%20coral%20and%20cream%20waves%2C%20minimal%20modern%20webtoon%20style%20texture%2C%20smooth%20curves%2C%20gentle%20light%2C%20clean%20background&width=1600&height=500&seq=cta-home&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-primary-950/60"></div>
        <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-4 py-16 text-center md:px-6 md:py-24">
          <h2 className="font-heading text-2xl font-black text-background-50 md:text-4xl">还在为选哪部韩漫发愁？</h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100 md:text-base">
            从人气榜、高分榜到题材分类，一站式帮你锁定当前最值得追的韩漫。榜单实时更新，喜欢的作品一眼找到。
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
            <Link to="/ranking" className="whitespace-nowrap rounded-full bg-background-50 px-7 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-secondary-200">
              立即查看榜单
            </Link>
            <Link to="/genres" className="whitespace-nowrap rounded-full border border-background-50/40 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10">
              按题材探索
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}